<script setup>
  import { computed, nextTick, onMounted, ref, watch } from "vue"
  import Swal from "sweetalert2"
  import vSelect from "vue-select"
  import "vue-select/dist/vue-select.css"
  import HabitacionesForm from "@/components/cotizador/HabitacionesForm.vue"
  import FiltrosOpcionalesForm from "@/components/cotizador/FiltrosOpcionalesForm.vue"
  import { useCancelacionGratuita } from "@/composables/useCancelacionGratuita"
  import ResultadoVuelos from "@/components/cotizador/ResultadoVuelos.vue"
  import VueloManualModal from "@/components/cotizador/VueloManualModal.vue"
  import FechaDatePicker from "@/components/cotizador/FechaDatePicker.vue"
  import api from "@/api/axios"

  import { useFechas } from "@/composables/useFechas"
  import { useVuelos } from "@/composables/useVuelos"
  import { useHabitaciones } from "@/composables/useHabitaciones"
  import { useVueloManual } from "@/composables/useVueloManual"

  import { useCotizadorState } from "@/composables/useCotizadorState"
  import { useCotizadorComputed } from "@/composables/useCotizadorComputed"
  import { useCotizadorWatchers } from "@/composables/useCotizadorWatchers"
  import { useCotizadorActions } from "@/composables/useCotizadorActions"
  import { useOrigenOtros } from "@/composables/useOrigenOtros"
  import { configDestinos } from "@/utils/destinosConfig"
  import { destinosDisponibles } from "@/utils/destinos"
  import { es } from "date-fns/locale"

  const props = defineProps({
    // true cuando la ventana está visible (no minimizada)
    activa: {
      type: Boolean,
      default: true
    },
    // sesión persistida a restaurar: { datos, job, estado }
    sesion: {
      type: Object,
      default: null
    }
  })

  const emit = defineEmits([
    "update:estado",
    "update:resumen",
    "update:job",
    "update:datos",
    "update:pdfs"
  ])

  // =========================
  // ALERTAS POR VENTANA
  // =========================
  // Si la ventana está minimizada, las alertas se encolan
  // y se muestran al restaurarla (no interrumpen otras ventanas)

  const alertasPendientes = []

  function notificar(opts) {
    if (props.activa) {
      return Swal.fire(opts)
    }
    alertasPendientes.push(opts)
  }

  watch(() => props.activa, async (visible) => {
    if (!visible) return
    while (alertasPendientes.length) {
      await Swal.fire(alertasPendientes.shift())
    }
  })

  function onJob(job) {
    emit("update:job", job)
  }

  const cotizacionGenerada = ref(false)

  // quoteCode de la última cotización generada: al resolverse, se consultan
  // sus PDF (mismo endpoint que usa el Cotizador Manual, ver
  // useCotizadorManual.js) para que la ventana pueda ofrecer descargarlos de
  // inmediato. Perú genera 2 PDF (con guía / sin guía) bajo el mismo
  // quoteCode, por eso el endpoint devuelve una lista y no un solo link.
  const quoteCode = ref(null)

  watch(quoteCode, async (code) => {
    if (!code) {
      emit("update:pdfs", [])
      return
    }

    try {
      const { data } = await api.get(`/quotes/by-code/${code}`)
      emit("update:pdfs", data.pdfs || [])
    } catch (err) {
      console.error(err)
      emit("update:pdfs", [])
    }
  })

  const state = useCotizadorState()

  const {
    sinVuelo,
    soloDirectos,
    destino,
    origen,
    telefono,
    visa,
    modalVueloVisible,
    recobro,
    valorRecobro,
    filtrosOpcionales,
    cancelacionGratuita
  } = state

  const {
    fechaIda,
    fechaRegreso,
    dias,
    dias1,
    dias2,
    bloqueoRegreso,
    minFecha
  } = useFechas(destino)

  const { mostrarCancelacionGratuita } =
    useCancelacionGratuita(fechaIda, cancelacionGratuita)

  const minFechaRegreso = computed(() => {

    if (!fechaIda.value) {
      return minFecha
    }

    const fecha = new Date(fechaIda.value)

    fecha.setDate(fecha.getDate() + 1)

    return fecha

  })
  const vuelos = useVuelos({ notificar, onJob })

  const {
    buscarVuelos,
    reanudarBusqueda,
    generarCotizacion,
    reanudarCotizacion,
    resultados,
    loading,
    searchId
  } = vuelos


  const habitacionesState =
    useHabitaciones()

  const {
    habitaciones,
    agregarHabitacion,
    eliminarHabitacion,
    validarPasajeros,
    totales,
    setAdultos,
    setNinos,
    setInfantes,
    totalPersonas
  } = habitacionesState


  const vueloManual =
    useVueloManual()

  const {
    guardarVuelo,

    multiDestino,

    ida,
    intermedio,
    regreso,

    precioSimple,
    preciosMulti
  } = vueloManual


  const computedState =
    useCotizadorComputed(
      destino,
      habitaciones
    )

  const hayResultados = computed(() => {

    const vuelos = resultados.value?.vuelos

    if (!vuelos) {
      return false
    }

    // SIMPLE
    if (vuelos.ida && vuelos.regreso) {
      return true
    }

    // MULTIDESTINO
    if (
      Array.isArray(vuelos.resultados) &&
      vuelos.resultados.length
    ) {
      return true
    }

    // DECAMERON
    if (
      Array.isArray(vuelos) &&
      vuelos.length
    ) {
      return true
    }

    return false

  })
  const cotizando = ref(false)

  const {

    showVisa,

    telefonoClass,

    destinoClass,

    showDiasSimple,

    showDiasCombinado,

    opcionesDias,

    labelsCombinado,

    minDias1,

    minDias2,

    colClass

  } = computedState


  useCotizadorWatchers({
    destino,
    dias,
    dias1,
    dias2,
    sinVuelo
  })

  const actions =
    useCotizadorActions({
      sinVuelo,
      origen,
      destino,
      fechaIda,
      fechaRegreso,
      habitaciones,
      telefono,
      dias,
      dias1,
      dias2,
      soloDirectos,
      visa,
      filtrosOpcionales,
      cancelacionGratuita,
      buscarVuelos,
      validarPasajeros,
      totales,
      resultados,
      loading,
      generarCotizacion,
      searchId,
      guardarVuelo,
      modalVueloVisible,
      valorRecobro,
      hayResultados,
      cotizando,
      quoteCode,
      notificar
    })

  const {
    handleBuscar,
    handleCotizar,
    handleGuardarVuelo,
    handleAbrirVueloManual
  } = actions

  // =========================
  // ESTADO PARA EL GESTOR DE VENTANAS
  // =========================

  const estadoBase = ref("editando")

  let huboProceso = false

  watch(loading, (v) => {
    if (v) huboProceso = true
  })

  const estadoVentana = computed(() => {
    if (cotizando.value) return "cotizando"
    if (loading.value) return "buscando"
    if (cotizacionGenerada.value) return "completada"
    return estadoBase.value
  })

  watch(estadoVentana, (v) => {
    emit("update:estado", v)
  }, { immediate: true })

  // =========================
  // PERSISTENCIA DE LA SESIÓN
  // =========================

  function snapshotDatos() {
    return {
      sinVuelo: sinVuelo.value,
      soloDirectos: soloDirectos.value,
      destino: destino.value,
      origen: origen.value,
      telefono: telefono.value,
      visa: visa.value,
      recobro: recobro.value,
      valorRecobro: valorRecobro.value,
      filtrosOpcionales: JSON.parse(JSON.stringify(filtrosOpcionales.value)),
      cancelacionGratuita: cancelacionGratuita.value,
      fechaIda: fechaIda.value,
      fechaRegreso: fechaRegreso.value,
      dias: dias.value,
      dias1: dias1.value,
      dias2: dias2.value,
      habitaciones: JSON.parse(JSON.stringify(habitaciones.value)),
      searchId: searchId.value,
      resultados: JSON.parse(JSON.stringify(resultados.value)),
      quoteCode: quoteCode.value
    }
  }

  watch(
    [
      sinVuelo, soloDirectos, destino, origen, telefono, visa,
      recobro, valorRecobro, filtrosOpcionales, cancelacionGratuita, fechaIda, fechaRegreso,
      dias, dias1, dias2, habitaciones, searchId, resultados, quoteCode
    ],
    () => emit("update:datos", snapshotDatos()),
    { deep: true, immediate: true }
  )

  async function restaurarSesion() {

    const d = props.sesion?.datos

    if (!d) return

    sinVuelo.value = !!d.sinVuelo
    soloDirectos.value = !!d.soloDirectos
    destino.value = d.destino || "PUJ"
    origen.value = d.origen || "MDE"
    telefono.value = d.telefono || ""
    visa.value = d.visa || "sin_visa"
    recobro.value = !!d.recobro
    valorRecobro.value = d.valorRecobro || 0
    filtrosOpcionales.value = Array.isArray(d.filtrosOpcionales) ? d.filtrosOpcionales : []

    if (Array.isArray(d.habitaciones) && d.habitaciones.length) {
      habitaciones.value = JSON.parse(JSON.stringify(d.habitaciones))
    }

    searchId.value = d.searchId || null

    if (d.resultados) {
      resultados.value = JSON.parse(JSON.stringify(d.resultados))
    }

    // esperar a que corran los watchers de destino (resetean días)
    await nextTick()

    dias.value = d.dias
    dias1.value = d.dias1
    dias2.value = d.dias2
    fechaIda.value = d.fechaIda

    // esperar al watcher que recalcula fechaRegreso desde fechaIda
    await nextTick()

    fechaRegreso.value = d.fechaRegreso

    // después de fechaIda: el toggle solo existe si la fecha cumple la regla
    cancelacionGratuita.value = !!d.cancelacionGratuita

    // esperar al watcher que resetea cotizacionGenerada al cambiar fechas
    await nextTick()

    cotizacionGenerada.value = props.sesion.estado === "completada"
    estadoBase.value = props.sesion.estado === "error" ? "error" : "editando"
    quoteCode.value = d.quoteCode || null
  }

  onMounted(async () => {

    if (!props.sesion) return

    await restaurarSesion()

    const job = props.sesion.job

    if (!job?.jobId) return

    // retomar el proceso que quedó corriendo antes de recargar
    if (job.tipo === "buscar") {

      const res = await reanudarBusqueda(job.jobId)

      if (res && hayResultados.value) {
        await onCotizar()
      } else if (!res) {
        estadoBase.value = "error"
      }

    } else if (job.tipo === "cotizar") {

      cotizando.value = true

      try {
        const ok = await reanudarCotizacion(job.jobId)
        if (ok) {
          cotizacionGenerada.value = true
          if (ok.quoteCode) quoteCode.value = ok.quoteCode
        } else {
          estadoBase.value = "error"
        }
      } finally {
        cotizando.value = false
      }

    }

  })


  const {
    origenesDisponibles,
    origenSelect,
    usandoOtroOrigen,
    otrosOrigenesDisponibles,
    otroOrigenSeleccionado
  } = useOrigenOtros(origen, destino)

  const formatoCOP = computed({
    get() {
      if (!valorRecobro.value) return ""

      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0
      }).format(valorRecobro.value)
    },
    set(value) {
      const numero = value.replace(/\D/g, "")
      valorRecobro.value = numero ? Number(numero) : null
    }
  })

  watch(destino, (nuevoDestino) => {
    console.log('Destino:', nuevoDestino)
    const config = configDestinos[nuevoDestino]
    console.log('Config:', config)

    if (
      config?.tipo === "simple" &&
      config.opciones?.length
    ) {

      dias.value = config.opciones[0]

    }

  }, { immediate: true })

  watch([fechaIda, fechaRegreso, destino, origen], () => {
    cotizacionGenerada.value = false
    estadoBase.value = "editando"
    quoteCode.value = null
  })

  watch([origen, destino], () => {
    emit(
      "update:resumen",
      `${origen.value || "?"} → ${destino.value || "?"}`
    )
  }, { immediate: true })

  const telefonoCols = computed(() => {
    if (recobro.value && showVisa.value) return "col-md-2"
    if (recobro.value) return "col-md-4"
    return telefonoClass.value
  })

  const fechaCols = computed(() => {
    if (recobro.value && showVisa.value) return "col-md-2"
    if (recobro.value) return "col-md-3"
    return "col-md-3"
  })

  async function onBuscar() {
    cotizacionGenerada.value = false
    estadoBase.value = "editando"
    quoteCode.value = null
    huboProceso = false
    const ok = await handleBuscar()
    if (ok) cotizacionGenerada.value = true
    else if (huboProceso) estadoBase.value = "error"
  }

  async function onCotizar() {
    cotizando.value = true
    try {
      const ok = await handleCotizar()
      if (ok) cotizacionGenerada.value = true
      else estadoBase.value = "error"
    } finally {
      cotizando.value = false
    }
  }
  const disabledDatesIda = (date) => {
    if (!destino.value?.includes("BKK")) {
      return false
    }

    // Deshabilita todo excepto Dom, Mar, Jue, Vie
    return ![0, 2, 4, 5].includes(date.getDay())
  }

  watch(destino, (nuevoDestino) => {
    if (nuevoDestino === "BKK" && fechaIda.value) {
      const day = new Date(fechaIda.value).getDay()

      if (![0, 2, 4, 5].includes(day)) {
        fechaIda.value = null
      }
    }
  })

  const destinoSeleccionado = computed({
    get: () =>
      destinosDisponibles.find(
        d => d.value === destino.value
      ) || null,

    set: (nuevo) => {
      destino.value = nuevo?.value || ""
    }
  })

  watch(
    () => destino.value,
    (value) => {
      if (!value) {
        destino.value = "PUJ"
      }
    },
    { immediate: true }
  )

  const maxFecha = computed(() => {
    const fecha = new Date()

    fecha.setMonth(fecha.getMonth() + 10)
    fecha.setDate(fecha.getDate() + 15)

    return fecha
  })

  watch(modalVueloVisible, (v) => {
    console.log("PADRE modalVueloVisible:", v)
  })
</script>

<template>

  <div class="container py-2" style="background-color: #f8f9fa; border-radius: 6px;">

    <!-- TOGGLES -->
    <div class="d-flex justify-content-center gap-4 flex-wrap mb-4">

      <!-- SIN VUELO -->
      <div class="form-check form-switch">

        <input class="form-check-input" type="checkbox" v-model="sinVuelo">

        <label class="form-check-label d-flex align-items-center gap-2">

          <i class="bi bi-x-circle"></i>

          Sin vuelo

        </label>

      </div>

      <!-- SOLO DIRECTOS -->
      <div v-if="!sinVuelo" class="form-check form-switch">

        <input class="form-check-input" type="checkbox" v-model="soloDirectos">

        <label class="form-check-label d-flex align-items-center gap-2">

          <i class="bi bi-signpost-2"></i>

          Solo directos

        </label>

      </div>

      <!-- RECOBRO -->
      <div class="form-check form-switch">

        <input class="form-check-input" type="checkbox" v-model="recobro">

        <label class="form-check-label d-flex align-items-center gap-2">

          <i class="bi bi-cash-stack"></i>

          Recobro

        </label>

      </div>

      <!-- CANCELACIÓN GRATUITA (solo viajes a 2 semanas o menos) -->
      <div v-if="mostrarCancelacionGratuita" class="form-check form-switch">

        <input class="form-check-input" type="checkbox" v-model="cancelacionGratuita">

        <label class="form-check-label d-flex align-items-center gap-2">

          <i class="bi bi-calendar-x"></i>

          Sin Cancelación gratuita

        </label>

      </div>

    </div>

    <!-- FILTROS OPCIONALES DE HOTEL -->
    <FiltrosOpcionalesForm v-model="filtrosOpcionales" class="mb-4" />

    <!-- FORM -->
    <form class="row g-3" @submit.prevent="onBuscar">

      <!-- ORIGEN -->
      <div class="col-md-6">

        <label class="form-label">
          Origen
        </label>

        <select v-model="origenSelect" class="form-select">

          <option v-for="o in origenesDisponibles" :key="o.value" :value="o.value">
            {{ o.label }}
          </option>

        </select>

        <v-select v-if="usandoOtroOrigen" v-model="otroOrigenSeleccionado" :options="otrosOrigenesDisponibles"
          label="label" placeholder="Buscar otro origen (ciudad, código o país)..." class="mt-2">
        </v-select>

      </div>

      <!-- DESTINO -->
      <div :class="destinoClass">

        <label class="form-label">
          Destino
        </label>

        <v-select v-model="destinoSeleccionado" :options="destinosDisponibles" label="label"
          placeholder="Buscar destino...">
        </v-select>
      </div>

      <!-- DIAS SIMPLE -->
      <div v-if="showDiasSimple" class="col-md-2">

        <label class="form-label">
          Días
        </label>

        <select v-model="dias" class="form-select">

          <option v-for="d in opcionesDias" :key="d" :value="d">

            {{ d }} días

          </option>

        </select>

      </div>

      <!-- COMBINADO -->
      <div v-if="showDiasCombinado" class="col-md-3">

        <div class="row">

          <div class="col-6">

            <small>
              {{ labelsCombinado[0] }}
            </small>

            <input type="number" class="form-control" v-model="dias1" :min="minDias1">

          </div>

          <div class="col-6">

            <small>
              {{ labelsCombinado[1] }}
            </small>

            <input type="number" class="form-control" v-model="dias2" :min="minDias2">

          </div>

        </div>

      </div>

      <!-- FECHA IDA -->
      <div :class="fechaCols">

        <label class="form-label">
          Fecha ida
        </label>

        <FechaDatePicker v-model="fechaIda" :format-locale="es" locale="es" model-type="yyyy-MM-dd" format="yyyy-MM-dd"
          :min-date="minFecha" :max-date="maxFecha" :disabled-dates="disabledDatesIda" :enable-time-picker="false"
          auto-apply />



      </div>

      <!-- FECHA REGRESO -->
      <div :class="fechaCols">

        <label class="form-label">
          Fecha regreso
        </label>


        <FechaDatePicker v-model="fechaRegreso" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
          format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false" :min-date="minFechaRegreso"
          :max-date="maxFecha" :disabled="bloqueoRegreso" required />



      </div>

      <!-- TELEFONO -->
      <!-- TELEFONO -->
      <div :class="telefonoCols">

        <label class="form-label">
          Teléfono
        </label>

        <input type="tel" class="form-control" v-model="telefono" required>

      </div>

      <!-- RECOBRO -->
      <div v-if="recobro" :class="showVisa ? 'col-md-3' : 'col-md-2'">

        <label class="form-label">
          Valor recobro
        </label>

        <input type="text" class="form-control" v-model="formatoCOP" placeholder="$ 0">

      </div>

      <!-- VISA -->
      <div v-if="showVisa" class="col-md-3">

        <label class="form-label">
          Visa
        </label>

        <select v-model="visa" class="form-select">
          <option value="sin_visa">Sin Visa</option>
          <option value="visa_americana">Visa Americana</option>
          <option value="visa_canadiense">Visa Canadiense</option>
          <option value="ambas_visas">Ambas Visas</option>
        </select>

      </div>

      <!-- HABITACIONES -->
      <HabitacionesForm :habitaciones="habitaciones" :colClass="colClass" :totalPersonas="totalPersonas"
        :agregarHabitacion="agregarHabitacion" :eliminarHabitacion="eliminarHabitacion" @update:adultos="
          setAdultos(
            $event.index,
            $event.value
          )
          " @update:ninos="
            setNinos(
              $event.index,
              $event.value
            )
            " @update:infantes="
              setInfantes(
                $event.index,
                $event.value
              )
              " />

      <!-- BOTON BUSCAR -->
      <div v-if="!sinVuelo" class="col-md-6">

        <button type="submit" class="btn btn-primary w-100" :disabled="loading || cotizando">
          {{
          cotizando
          ? "Generando cotización..."
          : loading
          ? "Buscando vuelos..."
          : "Buscar vuelos"
          }}
        </button>

      </div>

      <!-- BOTON MANUAL -->
      <div v-if="!sinVuelo" class="col-md-6">

        <button type="button" class="btn btn-success w-100" @click="handleAbrirVueloManual">

          Agregar vuelo manual

        </button>

      </div>

      <div v-if="(hayResultados || sinVuelo) && !cotizacionGenerada" class="col-12">
        <button type="button" class="btn btn-warning w-100" :disabled="cotizando || loading" @click="onCotizar">
          {{ cotizando ? 'Generando cotización...' : 'Generar cotización' }}
        </button>
      </div>

    </form>

    <div v-if="loading && !cotizando" class="text-center mt-5">

      <div class="spinner-border text-primary" style="
      width: 4rem;
      height: 4rem;
    "></div>

      <h5 class="mt-4 fw-bold">

        Buscando vuelos...

      </h5>

      <p class="text-muted mb-0">
        Esto puede tardar un momento
      </p>

    </div>

    <div v-if="cotizando" class="text-center mt-5">

      <div class="spinner-border text-warning" style="
      width: 4rem;
      height: 4rem;
    "></div>

      <h5 class="mt-4 fw-bold">

        Generando cotización...

      </h5>

      <p class="text-muted mb-0">

        Preparando precios y PDF

      </p>

    </div>

    <ResultadoVuelos v-if="hayResultados" :resultados="resultados" />

    <VueloManualModal :visible="modalVueloVisible" :multiDestino="multiDestino" :ida="ida" :intermedio="intermedio"
      :regreso="regreso" :precioSimple="precioSimple" :preciosMulti="preciosMulti" :fechaIda="fechaIda"
      :fechaRegreso="fechaRegreso" @close="
        modalVueloVisible = false
        " @guardar="handleGuardarVuelo" @update:multiDestino="
          multiDestino = $event
          " @update:precioSimple="
            precioSimple = $event
            " />

  </div>

</template>